import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { endOfMonth } from 'date-fns/endOfMonth'
import { endOfWeek } from 'date-fns/endOfWeek'
import { format } from 'date-fns/format'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfWeek } from 'date-fns/startOfWeek'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAnalytics } from 'hooks'
import { apiCall } from 'modules/core/services/api'
import { WeeklyQueueService } from 'openapi/services/WeeklyQueueService'
import { extractApiError } from '../../creative/utils/extract-api-error'
import { FAILED_MEDIA_SOURCE, toMediaSource } from '../../creative/components/AssetMedia'
import { useEditsApplying } from '../../creative/components/EditsApplyingBadge'
import {
  normalizeShape,
  toFailedPlatforms,
  toPublishedPlatformNames,
  type FailedPlatform,
} from '../../creative/components/post-card-chips'
import { ScheduledPostCard } from '../../creative/components/ScheduledPostCard'
import type { ContentShape } from '../../creative/store/idea-store'
import { isCreativeGenerationFinalized } from '../analytics/calendar-generation-tracking'
import { useCalendarStore } from '../store/calendar-store'
import { composerUrlForEntry } from '../composer-url'
import type { CalendarEntry } from '../types'

/**
 * Decide whether a calendar entry is still generating.
 *
 * Ordering matters:
 *   1. Trust an explicit server `generationStatus` (survives refresh): pending/
 *      generating = generating; ready/failed = done even without a thumbnail.
 *   2. Otherwise fall back to the legacy heuristic (creativeId + no thumbnail +
 *      no local finalize event) so autopilot-created creatives and rows without
 *      generationStatus still behave.
 *
 * Exported so CalendarDayCell (month bars + week wrapper) shares one definition.
 */
export function computeIsGenerating(entry: CalendarEntry): boolean {
  if (!entry.creativeId) return false
  if (entry.generationStatus === 'pending' || entry.generationStatus === 'generating') return true
  if (entry.generationStatus === 'ready' || entry.generationStatus === 'failed') return false
  return !entry.thumbnailUrl && !isCreativeGenerationFinalized(entry.creativeId)
}

/**
 * A slot whose GENERATION terminally failed and produced nothing to show —
 * the server folds the suggestion lane's `data.suggestionStatus` into
 * `generationStatus`, so failed weekly-queue slots land here (they used to
 * arrive statusless and spin forever). Renders the failed-media plate + Retry
 * (the #2348 treatment) instead of a spinner or a mute grey icon. A failed
 * regeneration that still HAS an old thumbnail keeps rendering its media —
 * the PostModal carries the failure story there.
 *
 * Exported so CalendarDayCell (month bars) shares one definition.
 */
export function computeIsGenerationFailed(entry: CalendarEntry): boolean {
  return (
    !!entry.creativeId &&
    entry.generationStatus === 'failed' &&
    !entry.thumbnailUrl &&
    !entry.publishedAt
  )
}

/**
 * Current visible calendar range, read straight from the store so callers
 * inside event handlers never close over a stale week/month. Exported so
 * sibling components (CreatePostButton's BYO refetch) share one definition
 * instead of a second copy. CalendarPage keeps its own duplicate on purpose
 * — importing through this file from the page would create a cycle via the
 * components barrel.
 */
export function getViewDateRange() {
  const { currentDate, view } = useCalendarStore.getState()
  if (view === 'week') {
    return {
      from: format(startOfWeek(currentDate), 'yyyy-MM-dd'),
      to: format(endOfWeek(currentDate), 'yyyy-MM-dd'),
    }
  }
  return {
    from: format(startOfWeek(startOfMonth(currentDate)), 'yyyy-MM-dd'),
    to: format(endOfWeek(endOfMonth(currentDate)), 'yyyy-MM-dd'),
  }
}

interface CalendarPopoverCardProps {
  entry: CalendarEntry
  onClose: () => void
  /** Opens PostModal — the single hub for schedule / approve / view-live. */
  onOpenPost?: (entry: CalendarEntry) => void
  /** True while the parent is loading this post's PostModal payload. */
  opening?: boolean
}

/**
 * A scheduled/published post rendered as a clickable media tile — the Calendar's
 * adoption of the Home `IdeaCard` / Weekly Cockpit `QueuedPostCard` model. The
 * whole card opens `PostModal` (schedule / approve / view-live). The 1:1 asset
 * routes through `AssetMedia` so reels autoplay inline (+ mute chip) and
 * carousels swipe; two overlaid corner chips carry the shape badge (top-left)
 * and, once published, the live indicator (top-right). Pre-publish posts reveal
 * a hover ••• (Edit + Delete); a `needsApproval` post gets an acid Approve
 * footer. The card is width-agnostic (`w-full`) — callers own the width.
 */
export function CalendarPopoverCard({
  entry,
  onClose,
  onOpenPost,
  opening,
}: CalendarPopoverCardProps) {
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const fetchEntries = useCalendarStore(s => s.fetchEntries)
  const fetchUpcoming = useCalendarStore(s => s.fetchUpcoming)
  const approveCreative = useCalendarStore(s => s.approveCreative)

  const handleApprove = useCallback(async () => {
    if (!entry.creativeId) return
    try {
      await approveCreative(entry.creativeId)
      track(AnalyticsEvents.Posts.POST_APPROVED, {
        surface: 'calendar',
        creative_id: entry.creativeId,
        project_id: entry.projectId,
        autopilot_id: entry.autopilotId,
      })
    } catch (err) {
      // Store already reverted the optimistic change; surface the failure.
      // Relay the SERVER's message when it has one. A platform content refusal
      // ("Pinterest won't accept this post: the description is 1,540
      // characters...") is actionable and "try again" is actively wrong for it
      // — the retry fails identically until the user edits the post.
      console.error('Failed to approve creative:', err)
      toast.error(extractApiError(err, "We couldn't approve this post. Please try again."))
    }
  }, [entry.creativeId, entry.projectId, entry.autopilotId, approveCreative, track])

  const handleEdit = useCallback(() => {
    if (!entry.creativeId) return
    onClose()
    track(AnalyticsEvents.Calendar.CALENDAR_ENTRY_TAPPED, {
      entry_type: 'edit_in_composer',
      channel: entry.channel,
    })
    navigate(composerUrlForEntry(entry), {
      state: { source: 'calendar', entry_source: 'calendar_entry' },
    })
  }, [entry, navigate, onClose, track])

  const handleUnschedule = useCallback(async () => {
    if (!entry.creativeId) return
    try {
      // Clears `scheduledAt` (shared composer/calendar endpoint, which emits the
      // POST_UNSCHEDULED event server-side), then reconcile the grid + upcoming.
      await useCalendarStore.getState().unscheduleCreative(entry.creativeId)
      const { from, to } = getViewDateRange()
      fetchEntries(from, to)
      fetchUpcoming()
      onClose()
      toast.success('Moved to draft. Find it in Posts.')
    } catch (err) {
      // Store reverts its optimistic change; surface the failure.
      console.error('Failed to unschedule creative:', err)
      toast.error("We couldn't move this post to draft. Please try again.")
    }
  }, [entry.creativeId, fetchEntries, fetchUpcoming, onClose])

  const handleConfirmDelete = useCallback(async () => {
    if (!entry.creativeId) return
    try {
      // Clear the cell and suppress any autopilot slot that would otherwise
      // spring back on the next refetch, then reconcile via refetch.
      await useCalendarStore.getState().deleteAndDismissCreative(entry.creativeId)
      const { from, to } = getViewDateRange()
      fetchEntries(from, to)
      fetchUpcoming()
      onClose()
    } catch (err) {
      // Optimistic change already reverted by the store; surface the failure
      // and rethrow so the confirm dialog stays open for a retry.
      console.error('Failed to delete creative:', err)
      toast.error("We couldn't delete this post. Please try again.")
      throw err
    }
  }, [entry.creativeId, fetchEntries, fetchUpcoming, onClose])

  // Optimistic Retry: the regenerate endpoint deletes the failed row and mints
  // a fresh one in the same slot, so this card instance (keyed by entry.id)
  // lives only until a refetch swaps the entry — until then it shows the
  // generating snake.
  const [retrying, setRetrying] = useState(false)
  // The post-retry refetch is delayed so the fire-and-forget generation can
  // mint the fresh shell first (an instant refetch would blink the day empty).
  // Kept in a ref + cleared on unmount so a closed popover / left calendar
  // never fires an orphaned range fetch; AutopilotGenerationComplete still
  // reconciles the final state either way.
  const retryRefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (retryRefetchTimer.current) clearTimeout(retryRefetchTimer.current)
    },
    []
  )
  const handleRetryGeneration = useCallback(async () => {
    if (!entry.creativeId) return
    setRetrying(true)
    try {
      // `{}` not undefined: the generated client types the body as required.
      await apiCall(
        WeeklyQueueService.weeklyQueueControllerRegenerateV1,
        null,
        entry.creativeId,
        {}
      )
      retryRefetchTimer.current = setTimeout(() => {
        const { from, to } = getViewDateRange()
        fetchEntries(from, to)
        fetchUpcoming()
      }, 2000)
    } catch (err) {
      setRetrying(false)
      console.error('Failed to retry generation:', err)
      toast.error("We couldn't restart this post. Please try again.")
    }
  }, [entry.creativeId, fetchEntries, fetchUpcoming])

  // ─── Derived state ────────────────────────────────────────────────────────
  const isGenerating = computeIsGenerating(entry) || retrying
  const isGenerationFailed = computeIsGenerationFailed(entry) && !retrying
  const isPublished = entry.type === 'published' || !!entry.publishedAt
  // The tile's thumbnail lags the user's composition edits until the master
  // re-renders — chip it on pre-publish posts (published masters are final).
  const isEditsApplying = useEditsApplying(entry.creativeId) && !isPublished
  const isFailed = !!entry.publishFailure
  const needsApproval = !!entry.needsApproval
  const canEdit = !!entry.creativeId

  const shape = normalizeShape(entry.shape)

  // Only render the rich (video / carousel) media when its source is actually
  // present — otherwise downgrade to a static image so the card shows a clean
  // thumbnail instead of AssetMedia's eternal "generating" poster (a real
  // reel/carousel has no videoUrl/mediaItems until the server DTO carries them).
  const hasRichMedia =
    (shape === 'short_vertical_video' && !!entry.videoUrl) ||
    (shape === 'carousel' && (entry.mediaItems?.length ?? 0) > 0)
  const effectiveShape: ContentShape = hasRichMedia ? shape : 'static_image'
  const hasMedia = hasRichMedia || !!entry.thumbnailUrl
  // A failed generation has no media — hand AssetMedia the terminal-failed
  // source so it renders the "Couldn't generate this…" plate + Retry instead
  // of the mute grey shape icon.
  const mediaSource =
    hasMedia && !isGenerating
      ? toMediaSource(entry)
      : isGenerationFailed
        ? FAILED_MEDIA_SOURCE
        : null

  // Edit + Delete only apply before a post goes out — never on a published or
  // still-generating post. Published posts visit via the PostModal the card opens.
  const showMenu = canEdit && !isPublished && !isGenerating
  // Generating posts have nothing to open yet; everything else opens PostModal.
  const isClickable = !isGenerating && canEdit && !!onOpenPost

  const platforms = entry.platforms?.length ? entry.platforms : entry.channel ? [entry.channel] : []

  // Per-sibling failure list. When the server emitted `platformStatuses` (multi-
  // platform post), delegate to the shared helper. For a standalone single-
  // platform post the server intentionally omits platformStatuses — fall back
  // to the rollup `publishFailure` + `channel` so the failure surfaces there
  // too. `entry.id` doubles as the creativeId key in that fallback (there's
  // only one sibling), matching the retry hook up in the parent's onOpenPost.
  const failedPlatforms: FailedPlatform[] = entry.platformStatuses
    ? toFailedPlatforms(entry.platformStatuses)
    : entry.publishFailure && entry.channel && entry.creativeId
      ? [
          {
            creativeId: entry.creativeId,
            platform: entry.channel,
            reason: entry.publishFailure.reason,
          },
        ]
      : []
  const publishedPlatforms = toPublishedPlatformNames(entry.platformStatuses)

  return (
    <ScheduledPostCard
      testId="calendar-post-card"
      mediaSource={mediaSource}
      shape={shape}
      effectiveShape={effectiveShape}
      platforms={platforms}
      isGenerating={isGenerating}
      isPublished={isPublished}
      isFailed={isFailed}
      isNeedsApproval={needsApproval && canEdit}
      showMenu={showMenu}
      isClickable={isClickable}
      opening={opening}
      onOpen={() => onOpenPost?.(entry)}
      onEdit={handleEdit}
      onUnschedule={showMenu && !needsApproval ? handleUnschedule : undefined}
      onConfirmDelete={handleConfirmDelete}
      onApprove={handleApprove}
      // Retry regenerates in the same slot — autopilot drafts only (the
      // endpoint's contract). Other failed posts open PostModal for "New take".
      onRetryGeneration={
        isGenerationFailed && entry.autopilotId ? handleRetryGeneration : undefined
      }
      deleteDialogTitle="Delete this post?"
      deleteDialogDescription="All platform variants for this post will be permanently deleted. This cannot be undone."
      failedPlatforms={failedPlatforms}
      publishedPlatforms={publishedPlatforms}
      isEditsApplying={isEditsApplying}
      editsCreativeId={entry.creativeId}
      isUserUpload={entry.mediaOrigin === 'user'}
    />
  )
}
